import * as Sentry from '@sentry/browser';
import firebase from 'firebase';
import fb from 'firebase/app';
import { useEffect, useReducer, useState } from 'react';
import useSWR from 'swr';
import { UserData } from 'wavepaths-shared/core';

import { updateEmail, UpdateEmailStatus, updateUserData } from '../../auth';
import { getMyDetails } from '../api/bonnyApi';
import { GetMyDetailsResponse } from '../api/types';

export interface IUpdateUserData {
    name?: string;
    bio?: string;
    link?: string;
    profileImageUrl?: string;
}

// Using this bc of this issue https://github.com/alexluong/gatsby-packages/issues/21
/* by Jeffrey Meng | MIT License */
interface IUseAuthState {
    user?: fb.User | null;
    error?: fb.auth.Error;
    loading: boolean;
}

function useAuthState(firebase: typeof fb): IUseAuthState {
    const [auth, setAuth] = useState<fb.auth.Auth | undefined>(undefined);

    interface IAction {
        type: 'auth_state_changed' | 'error';
        user?: fb.User | null;
        error?: fb.auth.Error;
    }

    const [state, dispatch] = useReducer(
        (state: IUseAuthState, action: IAction) => {
            switch (action.type) {
                case 'auth_state_changed':
                    return {
                        ...state,
                        user: action.user,
                        loading: false,
                    };
                case 'error':
                    return {
                        ...state,
                        error: action.error,
                        loading: false,
                    };
            }
        },
        {
            user: undefined,
            loading: true,
            error: undefined,
        },
    );
    useEffect(() => {
        setAuth(firebase.auth());
    }, [firebase]);

    useEffect(() => {
        if (auth === undefined) return;

        const unsubscribe = auth.onAuthStateChanged(
            (user) => {
                dispatch({ type: 'auth_state_changed', user });
                Sentry.configureScope((scope) => {
                    if (user && user.email && user.uid) {
                        scope.setUser({ email: user.email, id: user.uid });
                    }
                });
            },
            (error) => {
                dispatch({ type: 'error', error });
            },
        );

        return () => {
            unsubscribe();
        };
    }, [auth]);
    return state;
}

interface IUseUserData {
    userData: UserData | undefined;
    firebaseUser: firebase.User | undefined;
    isLoaded: boolean;
    error: firebase.auth.Error | Error | undefined;
    mutateError?: Omit<UpdateEmailStatus, 'success'>;
    setEmail: (email: string) => Promise<void>;
    setUserData: (data: IUpdateUserData) => Promise<void>;
}

const options = {
    dedupingInterval: 5 * 60 * 1000,
};

const useUserData = (): IUseUserData => {
    const { user: firebaseUser, loading, error: authError } = useAuthState(fb);
    // const { mutate } = useSWRConfig();
    const [mutateError, setMutateError] = useState<Omit<UpdateEmailStatus, 'success'> | undefined>(undefined);
    const { error: fetchError, data, mutate } = useSWR<GetMyDetailsResponse, Error>(
        firebaseUser ? ['/my/details', firebaseUser.uid] : null,
        firebaseUser ? () => getMyDetails(firebaseUser) : null,
        options,
    );

    const userData = data?.user;

    const setEmail = async (email: string): Promise<void> => {
        setMutateError(undefined);
        if (userData && firebaseUser) {
            const result = await updateEmail(firebaseUser, email);
            if (result !== 'success') {
                setMutateError(result);
                throw new Error(result);
            } else {
                mutate({ user: { ...userData, email } });
            }
        }
    };

    const setUserData = async (data: IUpdateUserData): Promise<void> => {
        setMutateError(undefined);

        if (userData && data && firebaseUser) {
            const result = await updateUserData(firebaseUser, data);

            if (result !== 'success') {
                setMutateError(result);
                throw new Error(result);
            } else {
                mutate({ user: { ...userData, ...data } });
            }
        }
    };

    // !loading && !user && !userData -> this means you are not logged in | isLoaded should be true
    // !loading && user && !userData -> this means you are logged in, but the hook is still fetching data from database | isLoaded should be false
    // !loading && user && userData -> this means loading has finished and all fetching is complete | isLoaded should be true
    const isLoaded: boolean = (!loading && !!firebaseUser && !!userData) || (!loading && !firebaseUser && !userData);

    //TODO: Remove the below and fix the typings all over the file. Ideally rewrite and test this ugly legacy code
    // @ts-ignore
    return {
        userData,
        firebaseUser: firebaseUser ?? undefined,
        isLoaded,
        mutateError,
        error: fetchError || authError,
        setEmail,
        setUserData,
    };
};

export default useUserData;
